import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { MemberService } from "@/gen/omnios/v1/member_connect.js";
import { formatCurrency } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import { createClient } from "@connectrpc/connect";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Link,
	Outlet,
	createFileRoute,
	useLocation,
} from "@tanstack/react-router";
import {
	Calendar,
	Check,
	CreditCard,
	Crown,
	Loader2,
	Sparkles,
	Users,
	Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/subscription")({
	component: SubscriptionSettingsLayout,
});

const memberClient = createClient(MemberService, transport);

function formatTimestamp(ts: { seconds: bigint } | undefined): string {
	if (!ts) return "—";
	return new Date(Number(ts.seconds) * 1000).toLocaleDateString("ru-RU");
}

function formatShortDate(ts: { seconds: bigint } | undefined): string {
	if (!ts) return "—";
	const date = new Date(Number(ts.seconds) * 1000);
	return date.toLocaleDateString("ru-RU", { month: "short", day: "numeric" });
}

function getDaysUntil(ts: { seconds: bigint } | undefined): number {
	if (!ts) return 0;
	const now = Date.now();
	const target = Number(ts.seconds) * 1000;
	return Math.max(0, Math.ceil((target - now) / (1000 * 60 * 60 * 24)));
}

const STATUS_CONFIG: Record<
	string,
	{
		variant: "default" | "secondary" | "destructive" | "outline";
		color: string;
		pulseColor: string;
	}
> = {
	active: {
		variant: "default",
		color: "bg-green-500",
		pulseColor: "bg-green-400",
	},
	trialing: {
		variant: "secondary",
		color: "bg-blue-500",
		pulseColor: "bg-blue-400",
	},
	past_due: {
		variant: "destructive",
		color: "bg-red-500",
		pulseColor: "bg-red-400",
	},
	canceled: {
		variant: "outline",
		color: "bg-gray-500",
		pulseColor: "bg-gray-400",
	},
};

// Radial Progress Ring Component
function RadialProgress({
	value,
	size = 80,
	strokeWidth = 8,
	className,
}: {
	value: number;
	size?: number;
	strokeWidth?: number;
	className?: string;
}) {
	const [animatedValue, setAnimatedValue] = useState(0);
	const radius = (size - strokeWidth) / 2;
	const circumference = radius * 2 * Math.PI;
	const offset = circumference - (animatedValue / 100) * circumference;

	useEffect(() => {
		const timer = setTimeout(() => setAnimatedValue(value), 100);
		return () => clearTimeout(timer);
	}, [value]);

	const getColor = (pct: number) => {
		if (pct >= 90) return "stroke-red-500";
		if (pct >= 70) return "stroke-yellow-500";
		return "stroke-primary";
	};

	return (
		<svg
			width={size}
			height={size}
			className={cn("transform -rotate-90", className)}
		>
			<circle
				cx={size / 2}
				cy={size / 2}
				r={radius}
				strokeWidth={strokeWidth}
				className="stroke-muted fill-none"
			/>
			<circle
				cx={size / 2}
				cy={size / 2}
				r={radius}
				strokeWidth={strokeWidth}
				className={cn(
					"fill-none transition-all duration-1000 ease-out",
					getColor(value),
				)}
				strokeLinecap="round"
				strokeDasharray={circumference}
				strokeDashoffset={offset}
			/>
		</svg>
	);
}

// Pulsing Status Indicator
function StatusIndicator({ status }: { status: string }) {
	const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.canceled;

	return (
		<span className="relative flex h-3 w-3">
			<span
				className={cn(
					"absolute inline-flex h-full w-full animate-ping rounded-full opacity-75",
					config.pulseColor,
				)}
			/>
			<span
				className={cn(
					"relative inline-flex h-3 w-3 rounded-full",
					config.color,
				)}
			/>
		</span>
	);
}

const TAB_KEYS = [
	{
		href: "/settings/subscription",
		key: "subscription_tab_overview",
		exact: true,
	},
	{ href: "/settings/subscription/billing", key: "subscription_tab_billing" },
];

// Layout with tabs
function SubscriptionSettingsLayout() {
	const { t } = useTranslation("settings");
	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("subscription_breadcrumb") },
	]);

	const location = useLocation();
	const currentPath = location.pathname;

	return (
		<div>
			<nav className="-mx-6 -mt-4 flex border-b bg-muted/30">
				{TAB_KEYS.map((tab) => {
					const isActive = tab.exact
						? currentPath === tab.href
						: currentPath.startsWith(tab.href);

					return (
						<Link
							key={tab.href}
							to={tab.href}
							className={cn(
								"px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
								isActive
									? "border-primary text-foreground bg-background"
									: "border-transparent text-muted-foreground hover:text-foreground",
							)}
						>
							{t(tab.key)}
						</Link>
					);
				})}
			</nav>

			<div className="pt-5">
				<Outlet />
			</div>
		</div>
	);
}

// Overview Tab (default)
export function OverviewTab() {
	const { t } = useTranslation("settings");

	const { data: subData, isLoading: subLoading } = useQuery({
		queryKey: ["my-subscription"],
		queryFn: () => memberClient.getMySubscription({}),
	});

	const { data: usageData } = useQuery({
		queryKey: ["my-usage"],
		queryFn: () => memberClient.getMyUsage({}),
	});

	if (subLoading) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-48" />
				<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
					<Skeleton className="h-32" />
					<Skeleton className="h-32" />
					<Skeleton className="h-32" />
				</div>
			</div>
		);
	}

	const sub = subData?.subscription;
	const metrics = usageData?.metrics ?? [];

	return (
		<div className="space-y-6">
			{/* Hero Section: Current Plan */}
			{sub ? (
				<PlanHeroCard subscription={sub} />
			) : (
				<Card className="border-dashed">
					<CardContent className="flex flex-col items-center justify-center py-12 text-center">
						<Crown className="h-12 w-12 text-muted-foreground/50" />
						<h3 className="mt-4 text-lg font-semibold">
							{t("subscription_no_subscription")}
						</h3>
					</CardContent>
				</Card>
			)}

			{/* Usage Dashboard */}
			{metrics.length > 0 && (
				<div className="space-y-4">
					<h2 className="text-sm font-medium">
						{t("subscription_usage_title")}
					</h2>
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
						{metrics.map((m) => (
							<UsageMetricCard key={m.metricCode} metric={m} />
						))}
					</div>
				</div>
			)}

			{/* Contact hint */}
			<p className="text-sm text-muted-foreground">
				{t("subscription_plan_change_hint")}
			</p>
		</div>
	);
}

// Hero Section: Current Plan Card
function PlanHeroCard({
	subscription,
}: {
	subscription: {
		planName: string;
		planSlug: string;
		status: string;
		period: string;
		seatCount: number;
		currentPeriodStart?: { seconds: bigint };
		currentPeriodEnd?: { seconds: bigint };
		trialEnd?: { seconds: bigint };
	};
}) {
	const { t } = useTranslation("settings");
	const daysUntilRenewal = getDaysUntil(subscription.currentPeriodEnd);
	const config = STATUS_CONFIG[subscription.status] ?? STATUS_CONFIG.canceled;

	const renewalColor =
		daysUntilRenewal <= 3
			? "text-red-500"
			: daysUntilRenewal <= 7
				? "text-yellow-500"
				: "text-muted-foreground";

	return (
		<Card className="relative overflow-hidden border-0 bg-gradient-to-br from-primary/5 via-background to-primary/10 dark:from-primary/10 dark:to-primary/5">
			<div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />

			<CardHeader className="pb-2">
				<div className="flex items-center gap-3">
					<div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
						<Crown className="h-6 w-6 text-primary" />
					</div>
					<div>
						<div className="flex items-center gap-2">
							<CardTitle className="text-xl">{subscription.planName}</CardTitle>
							<div className="flex items-center gap-1.5">
								<StatusIndicator status={subscription.status} />
								<Badge variant={config.variant} className="text-xs">
									{t(`subscription_status_${subscription.status}` as any) ??
										subscription.status}
								</Badge>
							</div>
						</div>
						<p className="text-sm text-muted-foreground">
							{subscription.period === "annual"
								? t("subscription_period_annual")
								: t("subscription_period_monthly")}
						</p>
					</div>
				</div>
			</CardHeader>

			<CardContent>
				<div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
					<div className="rounded-lg bg-background/60 p-3 backdrop-blur-sm">
						<div className="flex items-center gap-2 text-muted-foreground">
							<Users className="h-4 w-4" />
							<span className="text-xs font-medium">
								{t("subscription_label_seats")}
							</span>
						</div>
						<p className="mt-1 text-2xl font-bold tabular-nums">
							{subscription.seatCount}
						</p>
					</div>

					<div className="rounded-lg bg-background/60 p-3 backdrop-blur-sm">
						<div className="flex items-center gap-2 text-muted-foreground">
							<Calendar className="h-4 w-4" />
							<span className="text-xs font-medium">
								{t("subscription_label_renewal")}
							</span>
						</div>
						<p
							className={cn(
								"mt-1 text-2xl font-bold tabular-nums",
								renewalColor,
							)}
						>
							{daysUntilRenewal} {t("subscription_days")}
						</p>
					</div>

					<div className="rounded-lg bg-background/60 p-3 backdrop-blur-sm">
						<div className="flex items-center gap-2 text-muted-foreground">
							<Zap className="h-4 w-4" />
							<span className="text-xs font-medium">
								{t("subscription_label_period")}
							</span>
						</div>
						<p className="mt-1 text-lg font-semibold">
							{formatShortDate(subscription.currentPeriodStart)} —{" "}
							{formatShortDate(subscription.currentPeriodEnd)}
						</p>
					</div>

					<div className="rounded-lg bg-background/60 p-3 backdrop-blur-sm">
						<div className="flex items-center gap-2 text-muted-foreground">
							<CreditCard className="h-4 w-4" />
							<span className="text-xs font-medium">
								{t("subscription_label_next_payment")}
							</span>
						</div>
						<p className="mt-1 text-lg font-semibold">
							{formatTimestamp(subscription.currentPeriodEnd)}
						</p>
					</div>
				</div>

				{subscription.trialEnd && (
					<div className="mt-4 flex items-center gap-2 rounded-lg bg-blue-500/10 px-3 py-2 text-sm text-blue-600 dark:text-blue-400">
						<Sparkles className="h-4 w-4" />
						{t("subscription_label_trial_until")}:{" "}
						{formatTimestamp(subscription.trialEnd)}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

// Usage Metric Card with Radial Progress
function UsageMetricCard({
	metric,
}: {
	metric: {
		metricCode: string;
		metricName: string;
		currentUsage: bigint;
		includedQuantity: bigint;
		overageUnitPrice?: bigint;
	};
}) {
	const { t } = useTranslation("settings");
	const included = Number(metric.includedQuantity);
	const current = Number(metric.currentUsage);
	const pct = included > 0 ? Math.min(100, (current / included) * 100) : 0;
	const isOver = current > included && included > 0;
	const overage = isOver ? current - included : 0;
	const overagePrice = metric.overageUnitPrice
		? Number(metric.overageUnitPrice)
		: 0;

	const formatValue = (val: number) => {
		if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
		if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
		return val.toLocaleString();
	};

	return (
		<Card className="group relative overflow-hidden transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
			<CardContent className="flex flex-col items-center p-5">
				<div className="relative">
					<RadialProgress value={pct} size={80} strokeWidth={8} />
					<div className="absolute inset-0 flex items-center justify-center">
						<span
							className={cn(
								"text-lg font-bold tabular-nums",
								pct >= 90
									? "text-red-500"
									: pct >= 70
										? "text-yellow-500"
										: "text-foreground",
							)}
						>
							{Math.round(pct)}%
						</span>
					</div>
				</div>

				<h4 className="mt-3 text-sm font-medium text-muted-foreground">
					{metric.metricName}
				</h4>

				<p className="mt-1 text-base font-semibold tabular-nums">
					{formatValue(current)}/{formatValue(included)}
				</p>

				{isOver && overagePrice > 0 && (
					<div className="mt-2 rounded-md bg-red-500/10 px-2 py-1 text-xs text-red-500">
						+{formatCurrency(overage * overagePrice)}{" "}
						{t("subscription_overage")}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

// Billing Details Tab
export function BillingTab() {
	const { t } = useTranslation("settings");
	const queryClient = useQueryClient();

	const { data, isLoading } = useQuery({
		queryKey: ["billing-details"],
		queryFn: () => memberClient.getBillingDetails({}),
	});

	const [companyName, setCompanyName] = useState("");
	const [taxId, setTaxId] = useState("");
	const [billingEmail, setBillingEmail] = useState("");
	const [addressLine1, setAddressLine1] = useState("");
	const [city, setCity] = useState("");
	const [postalCode, setPostalCode] = useState("");
	const [dirty, setDirty] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);

	useEffect(() => {
		if (data?.details) {
			const d = data.details;
			setCompanyName(d.companyName);
			setTaxId(d.taxId);
			setBillingEmail(d.billingEmail);
			setAddressLine1(d.addressLine1);
			setCity(d.city);
			setPostalCode(d.postalCode);
			setDirty(false);
		}
	}, [data]);

	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!dirty) return;
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(async () => {
			setSaving(true);
			setSaved(false);
			try {
				await memberClient.updateBillingDetails({
					details: {
						companyName,
						taxId,
						billingEmail,
						addressLine1,
						addressLine2: "",
						city,
						postalCode,
						country: "RU",
					},
				});
				queryClient.invalidateQueries({ queryKey: ["billing-details"] });
				setDirty(false);
				setSaved(true);
				setTimeout(() => setSaved(false), 2000);
			} finally {
				setSaving(false);
			}
		}, 800);
		return () => {
			if (timer.current) clearTimeout(timer.current);
		};
	}, [
		dirty,
		companyName,
		taxId,
		billingEmail,
		addressLine1,
		city,
		postalCode,
		queryClient,
	]);

	if (isLoading) {
		return (
			<div className="max-w-2xl space-y-4">
				<Skeleton className="h-16" />
				<Skeleton className="h-16" />
				<Skeleton className="h-16" />
			</div>
		);
	}

	const field = (
		label: string,
		value: string,
		setter: (v: string) => void,
		placeholder?: string,
	) => (
		<div className="space-y-1.5">
			<Label>{label}</Label>
			<Input
				value={value}
				onChange={(e) => {
					setter(e.target.value);
					setDirty(true);
				}}
				placeholder={placeholder}
			/>
		</div>
	);

	return (
		<div className="max-w-2xl space-y-6">
			<div className="flex items-center gap-2">
				<h3 className="text-sm font-medium">
					{t("subscription_billing_title")}
				</h3>
				<div className="h-4">
					{saving && (
						<span className="flex items-center gap-1 text-xs text-muted-foreground">
							<Loader2 className="h-3 w-3 animate-spin" />
						</span>
					)}
					{saved && (
						<span className="flex items-center gap-1 text-xs text-green-600">
							<Check className="h-3 w-3" />
							{t("subscription_billing_saved")}
						</span>
					)}
				</div>
			</div>

			<div className="grid gap-4 sm:grid-cols-2">
				{field(
					t("subscription_billing_company"),
					companyName,
					setCompanyName,
					t("subscription_billing_company_placeholder"),
				)}
				{field(
					t("subscription_billing_tax_id"),
					taxId,
					setTaxId,
					t("subscription_billing_tax_placeholder"),
				)}
				{field(
					t("subscription_billing_email"),
					billingEmail,
					setBillingEmail,
					t("subscription_billing_email_placeholder"),
				)}
				{field(
					t("subscription_billing_address"),
					addressLine1,
					setAddressLine1,
					t("subscription_billing_address_placeholder"),
				)}
				{field(
					t("subscription_billing_city"),
					city,
					setCity,
					t("subscription_billing_city_placeholder"),
				)}
				{field(
					t("subscription_billing_postal"),
					postalCode,
					setPostalCode,
					t("subscription_billing_postal_placeholder"),
				)}
			</div>
		</div>
	);
}
